import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Link,
} from '@mui/material';
import {
  Timeline,
  CheckCircle,
  Schedule,
  Assignment,
  Phone,
  Email,
  Chat,
  AttachMoney,
  Note,
  SwapHoriz,
  Edit as EditIcon,
  Cancel,
} from '@mui/icons-material';
import { format, parseISO, isValid } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { enUS } from 'date-fns/locale';
import { es } from 'date-fns/locale';
import api from '../../services/api';
import toastError from '../../errors/toastError';
import { i18n } from '../../translate/i18n';
import { useTheme } from '@material-ui/core/styles';

const DealActivityHistory = ({ dealId, stages: stagesProp }) => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stagesLocal, setStagesLocal] = useState([]);
  const theme = useTheme();

  // Mesclar estágios do parent com os buscados (garante lookup de nomes em todas as pipelines)
  const stages = (() => {
    const fromLocal = stagesLocal || [];
    const fromProp = stagesProp || [];
    const byId = new Map();
    [...fromLocal, ...fromProp].forEach(s => {
      const id = s?.id;
      if (id != null && !byId.has(String(id))) byId.set(String(id), s);
    });
    return Array.from(byId.values());
  })();

  useEffect(() => {
    if (!dealId) return;
    fetchStages(); // Sempre buscar todos os estágios para lookup de nomes
    fetchActivities();
  }, [dealId]);

  const fetchStages = async () => {
    try {
      const { data } = await api.get('/pipelines');
      const allStages = [];
      (data || []).forEach(pipeline => {
        if (pipeline.stages) {
          pipeline.stages.forEach(stage => {
            allStages.push({
              ...stage,
              pipelineId: pipeline.id,
              pipelineName: pipeline.name,
            });
          });
        }
      });
      setStagesLocal(allStages);
    } catch (err) {
      console.error('Erro ao buscar estágios:', err);
    }
  };

  const fetchActivities = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/deals/${dealId}/activities`);
      setActivities(data);
    } catch (err) {
      console.error('Erro ao buscar atividades:', err);
      setError(i18n.t('pipelines.details.activityHistory.loadingError'));
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  // Para stage_moved: identidade visual por estágio de destino (Concluídos = sucesso, Perdidos = falha)
  const getStageMovedVariant = activity => {
    if (!activity || activity.type !== 'stage_moved') return null;
    const toName = (activity.data?.toStageName || '').toLowerCase().trim();
    if (/conclu[ií]do?s?/.test(toName)) return 'success';
    if (/perdido?s?/.test(toName)) return 'error';
    return null;
  };

  const getActivityIcon = (type, activity) => {
    if (type === 'stage_moved' && activity) {
      const variant = getStageMovedVariant(activity);
      if (variant === 'success') return <Timeline color='success' />;
      if (variant === 'error') return <Timeline color='error' />;
    }
    switch (type) {
      case 'requirement_completed':
        return <CheckCircle color='success' />;
      case 'requirement_created':
        return <Assignment color='primary' />;
      case 'stage_changed':
      case 'stage_moved':
        return <Timeline color='info' />;
      case 'phone_call':
        return <Phone color='primary' />;
      case 'email_sent':
        return <Email color='primary' />;
      case 'message_sent':
        return <Chat color='primary' />;
      case 'value_updated':
        return <AttachMoney color='success' />;
      case 'note_added':
        return <Note color='default' />;
      case 'schedule_created':
        return <Schedule color='warning' />;
      case 'pipeline_transferred':
        return <SwapHoriz color='info' />;
      case 'requirement_edited':
        return <EditIcon color='primary' />;
      case 'schedule_cancelled':
        return <Cancel color='error' />;
      case 'schedule_edited':
        return <EditIcon color='primary' />;
      case 'sale_edited':
        return <AttachMoney color='warning' />;
      default:
        return <Assignment color='default' />;
    }
  };

  const getActivityColor = (type, activity) => {
    if (type === 'stage_moved' && activity) {
      const variant = getStageMovedVariant(activity);
      if (variant) return variant;
    }
    switch (type) {
      case 'requirement_completed':
        return 'success';
      case 'requirement_created':
        return 'primary';
      case 'stage_changed':
      case 'stage_moved':
        return 'info';
      case 'phone_call':
      case 'email_sent':
      case 'message_sent':
        return 'primary';
      case 'value_updated':
        return 'success';
      case 'note_added':
        return 'default';
      case 'schedule_created':
        return 'warning';
      case 'pipeline_transferred':
        return 'info';
      case 'requirement_edited':
        return 'primary';
      case 'schedule_cancelled':
        return 'error';
      case 'schedule_edited':
        return 'primary';
      case 'sale_edited':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatActivityMessage = activity => {
    try {
      const { type, data } = activity || {};
      switch (type) {
        case 'requirement_completed':
          if (data?.requirementType === 'sale') {
            return 'Venda realizada';
          }
          return `Atividade ${data?.requirementName || '-'} foi concluída`;
        case 'requirement_created':
          return `Nova atividade ${data?.requirementName || '-'} foi adicionada`;
        case 'requirement_edited':
          return `Campo "${data?.requirementName || '-'}" alterado`;
        case 'stage_changed': {
          return `Deal movido de ${data?.fromStage || '-'} para ${data?.toStage || '-'}`;
        }
        case 'stage_moved': {
          const fromStage = stages.find(
            s => String(s?.id) === String(data?.fromStageId)
          );
          const toStage = stages.find(
            s => String(s?.id) === String(data?.toStageId)
          );
          const fromStageName =
            data?.fromStageName ||
            fromStage?.name ||
            `${i18n.t('pipelines.details.activityHistory.stagePrefix')} ${data?.fromStageId ?? '-'}`;
          const toStageName =
            data?.toStageName ||
            toStage?.name ||
            `${i18n.t('pipelines.details.activityHistory.stagePrefix')} ${data?.toStageId ?? '-'}`;
          return `Deal movido de ${fromStageName} para ${toStageName}`;
        }
        case 'phone_call':
          return `Ligação realizada com ${data?.contactName || 'cliente'}`;
        case 'email_sent':
          return `Email enviado para ${data?.contactEmail || 'cliente'}`;
        case 'message_sent':
          return 'Mensagem enviada via WhatsApp';
        case 'value_updated':
          return `Valor atualizado para R$ ${Number(
            data?.newValue ?? 0
          ).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
        case 'note_added':
          return `Nota adicionada: ${String(data?.note || '').substring(0, 50)}${(data?.note?.length || 0) > 50 ? '...' : ''}`;
        case 'pipeline_transferred':
          return `Transferido da pipeline ${data?.fromPipelineName || '-'} para ${data?.toPipelineName || '-'} pelo usuário ${activity?.user?.name || '-'}`;
        case 'schedule_created': {
          const productName = data?.productName || 'Agendamento';
          return `Agendamento realizado - ${productName}`;
        }
        case 'schedule_edited': {
          const displayLabel = data?.displayLabel;
          if (displayLabel) return displayLabel;
          const productName = data?.productName || 'Agendamento';
          return `Agendamento editado - ${productName}`;
        }
        case 'requirement_edited':
          return `Campo "${data?.requirementName || '-'}" alterado`;
        case 'schedule_cancelled': {
          const displayLabel = data?.displayLabel;
          if (displayLabel) return displayLabel;
          const productName = data?.productName || 'Agendamento';
          const dateTimeStr = data?.startDateTime;
          if (dateTimeStr) {
            try {
              const date = parseISO(dateTimeStr);
              if (isValid(date)) {
                return `Agendamento cancelado - ${productName} - ${format(
                  date,
                  'dd/MM/yyyy',
                  { locale: ptBR }
                )} às ${format(date, 'HH:mm', { locale: ptBR })}`;
              }
            } catch {
              // fall through
            }
          }
          return `Agendamento cancelado - ${productName}`;
        }
        case 'sale_edited': {
          const displayLabel = data?.displayLabel;
          if (displayLabel) return displayLabel;
          return 'Venda editada';
        }
        default:
          return null; // Tipo não tratado - atividade não será renderizada
      }
    } catch (err) {
      console.warn('Erro ao formatar mensagem da atividade:', err);
      return null;
    }
  };

  const handledActivityTypes = [
    'requirement_completed',
    'requirement_created',
    'requirement_edited',
    'stage_changed',
    'stage_moved',
    'phone_call',
    'email_sent',
    'message_sent',
    'value_updated',
    'note_added',
    'schedule_created',
    'schedule_cancelled',
    'schedule_edited',
    'sale_edited',
    'pipeline_transferred',
  ];

  const activitiesToRender = (activities || []).filter(a =>
    handledActivityTypes.includes(a?.type)
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity='error' sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!activitiesToRender || activitiesToRender.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', p: 3, color: 'text.secondary' }}>
        <Timeline sx={{ fontSize: 48, mb: 2, opacity: 0.5 }} />
        <Typography variant='body1'>
          {i18n.t('pipelines.details.activityHistory.noActivities')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      elevation={1}
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        backgroundColor: theme.palette.action.hover,
      }}
    >
      <Typography
        variant='h6'
        sx={{
          mb: 3,
          color: 'primary.main',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Timeline sx={{ mr: 1 }} />
        {i18n.t('pipelines.details.activityHistory.title')}
      </Typography>

      <List sx={{ p: 0 }}>
        {activitiesToRender.map((activity, index) => {
          const stageMovedVariant = getStageMovedVariant(activity);
          const isStageMovedSuccess = activity.type === 'stage_moved' && stageMovedVariant === 'success';
          const isStageMovedError = activity.type === 'stage_moved' && stageMovedVariant === 'error';
          return (
          <React.Fragment key={activity.id || index}>
            <ListItem
              sx={{
                px: 0,
                py: 2,
                alignItems: 'flex-start',
                ...(isStageMovedSuccess && {
                  borderLeft: '4px solid',
                  borderLeftColor: 'success.main',
                  backgroundColor: (t) => (t.palette.mode || t.palette.type) === 'dark' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(76, 175, 80, 0.08)',
                  borderRadius: 1,
                  mx: 0,
                  px: 2,
                  mt: 1,
                }),
                ...(isStageMovedError && {
                  borderLeft: '4px solid',
                  borderLeftColor: 'error.main',
                  backgroundColor: (t) => (t.palette.mode || t.palette.type) === 'dark' ? 'rgba(244, 67, 54, 0.15)' : 'rgba(244, 67, 54, 0.08)',
                  borderRadius: 1,
                  mx: 0,
                  px: 2,
                  mt: 1,
                }),
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                {getActivityIcon(activity.type, activity)}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant='body1' sx={{ flex: 1 }}>
                        {formatActivityMessage(activity)}
                      </Typography>
                      <Chip
                        label={(() => {
                          if (!activity.createdAt) return '-';
                          try {
                            const date = parseISO(activity.createdAt);
                            return isValid(date)
                              ? format(date, 'dd/MM HH:mm', { locale: ptBR })
                              : '-';
                          } catch {
                            return '-';
                          }
                        })()}
                        size='small'
                        color={getActivityColor(activity.type, activity)}
                        variant='outlined'
                        sx={{ ml: 2 }}
                      />
                    </Box>
                    {activity.type === 'requirement_edited' && activity.data && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant='body2' sx={{ mb: 1 }}>
                              <strong>Campo:</strong> {activity.data?.requirementName ?? '-'}
                            </Typography>
                            <Typography variant='body2' sx={{ mb: 0.5 }}>
                              <strong>Valor anterior:</strong>{' '}
                              {activity.data?.previousValue != null && activity.data?.previousValue !== ''
                                ? String(activity.data.previousValue)
                                : '(vazio)'}
                            </Typography>
                            <Typography variant='body2' sx={{ mb: 1 }}>
                              <strong>Novo valor:</strong>{' '}
                              {activity.data?.value != null && activity.data?.value !== ''
                                ? String(activity.data.value)
                                : '(vazio)'}
                            </Typography>
                            {activity.user?.name && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary' }}>
                                Alterado por: {activity.user.name}
                              </Typography>
                            )}
                            {activity.createdAt && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 0.25 }}>
                                {(() => {
                                  try {
                                    const date = parseISO(activity.createdAt);
                                    return isValid(date)
                                      ? format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR })
                                      : null;
                                  } catch {
                                    return null;
                                  }
                                })()}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                    )}
                    {activity.type === 'schedule_created' && activity.data && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant='body2' sx={{ mb: 1 }}>
                              <strong>Produto:</strong> {activity.data?.productName ?? '-'}
                            </Typography>
                            {activity.data?.title && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Título:</strong> {activity.data.title}
                              </Typography>
                            )}
                            {activity.data?.startDateTime && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Início:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.startDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.startDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.endDateTime && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Fim:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.endDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.endDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.location && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Local:</strong> {activity.data.location}
                              </Typography>
                            )}
                            {activity.user?.name && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 1 }}>
                                Agendado por: {activity.user.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                    )}
                    {activity.type === 'schedule_cancelled' && activity.data && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant='body2' sx={{ mb: 1 }}>
                              <strong>Produto:</strong> {activity.data?.productName ?? '-'}
                            </Typography>
                            {activity.data?.title && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Título:</strong> {activity.data.title}
                              </Typography>
                            )}
                            {activity.data?.startDateTime && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Data/Hora:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.startDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.startDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.location && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Local:</strong> {activity.data.location}
                              </Typography>
                            )}
                            {activity.data?.reason && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Motivo:</strong> {activity.data.reason === 'produto_removido_venda' ? 'Produto removido da venda' : activity.data.reason}
                              </Typography>
                            )}
                            {activity.user?.name && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 1 }}>
                                Cancelado por: {activity.user.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                    )}
                    {activity.type === 'schedule_edited' && activity.data && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography variant='body2' sx={{ mb: 1 }}>
                              <strong>Produto:</strong> {activity.data?.productName ?? '-'}
                            </Typography>
                            {activity.data?.title && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Título:</strong> {activity.data.title}
                              </Typography>
                            )}
                            {(activity.data?.previousStartDateTime || activity.data?.previousEndDateTime) && (
                              <Typography variant='body2' sx={{ mb: 0.5, mt: 1, fontWeight: 600 }}>
                                Antes:
                              </Typography>
                            )}
                            {activity.data?.previousStartDateTime && (
                              <Typography variant='body2' sx={{ mb: 1, pl: 1 }}>
                                <strong>Início:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.previousStartDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.previousStartDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.previousEndDateTime && (
                              <Typography variant='body2' sx={{ mb: 1, pl: 1 }}>
                                <strong>Fim:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.previousEndDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.previousEndDateTime;
                                })()}
                              </Typography>
                            )}
                            <Typography variant='body2' sx={{ mb: 0.5, mt: 1, fontWeight: 600 }}>
                              Depois:
                            </Typography>
                            {activity.data?.startDateTime && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Início:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.startDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.startDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.endDateTime && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Fim:</strong>{' '}
                                {(() => {
                                  try {
                                    const date = parseISO(activity.data.endDateTime);
                                    if (isValid(date)) {
                                      return format(date, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
                                    }
                                  } catch {
                                    // fall through
                                  }
                                  return activity.data.endDateTime;
                                })()}
                              </Typography>
                            )}
                            {activity.data?.location && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Local:</strong> {activity.data.location}
                              </Typography>
                            )}
                            {activity.user?.name && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 1 }}>
                                Editado por: {activity.user.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                    )}
                    {activity.type === 'sale_edited' && activity.data && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            {(activity.data?.previousTotalValue !== undefined && activity.data?.previousTotalValue !== null) && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Valor total da venda anterior:</strong> R${' '}
                                {Number(activity.data.previousTotalValue).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                })}
                              </Typography>
                            )}
                            {activity.data?.totalValue !== undefined && activity.data?.totalValue !== null && (
                              <Typography variant='body2' sx={{ mb: 1 }}>
                                <strong>Valor Total:</strong> R${' '}
                                {Number(activity.data.totalValue).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                })}
                              </Typography>
                            )}
                            {activity.data?.saleProducts && activity.data.saleProducts.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant='body2' sx={{ fontWeight: 600, mb: 1 }}>
                                  Produtos:
                                </Typography>
                                {activity.data.saleProducts.map((product, idx) => (
                                  <Box key={idx} sx={{ mb: 1, pl: 1, borderLeft: `2px solid ${theme.palette.primary.main}` }}>
                                    <Typography variant='body2' sx={{ fontWeight: 500 }}>
                                      {product.productName || product.name || '-'}
                                    </Typography>
                                    <Typography variant='caption' sx={{ display: 'block' }}>
                                      Quantidade: {product.quantity || '-'} | 
                                      Valor: R${' '}
                                      {product.productValue
                                        ? Number(product.productValue).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                          })
                                        : '-'}
                                    </Typography>
                                  </Box>
                                ))}
                              </Box>
                            )}
                            {activity.user?.name && (
                              <Typography variant='caption' display='block' sx={{ color: 'text.secondary', mt: 1 }}>
                                Editado por: {activity.user.name}
                              </Typography>
                            )}
                          </Box>
                        </Box>
                    )}
                    {activity.type === 'requirement_completed' &&
                      ((activity.data?.value !== undefined && activity.data?.value !== null) ||
                        (activity.data?.requirementType === 'attach' &&
                          (activity.data?.fileName || activity.data?.fileUrl))) && (
                        <Box sx={{ mt: 1 }}>
                          <Box
                            sx={{
                              p: 1.5,
                              backgroundColor: theme.palette.action.hover,
                              color: theme.palette.text.primary,
                              borderRadius: 1,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          >
                            <Typography
                              variant='body2'
                              sx={{ fontFamily: 'monospace' }}
                            >
                              {(() => {
                                const value = activity.data.value;
                                const requirementType =
                                  activity.data.requirementType;

                                // Se for uma venda, mostrar detalhes dos produtos
                                if (
                                  requirementType === 'sale' &&
                                  activity.data.saleProducts
                                ) {
                                  return (
                                    <Box>
                                      {activity.data.saleProducts.map(
                                        (product, index) => (
                                          <Box key={index} sx={{ mb: 1 }}>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontWeight: 500 }}
                                            >
                                              {product.productName}
                                            </Typography>
                                            <Typography variant='caption'>
                                              {i18n.t(
                                                'pipelines.details.activityHistory.sale.productDetails.quantity'
                                              )}
                                              : {product.quantity} |
                                              {i18n.t(
                                                'pipelines.details.activityHistory.sale.productDetails.value'
                                              )}
                                              : R${' '}
                                              {parseFloat(
                                                product.productValue
                                              ).toLocaleString('pt-BR', {
                                                minimumFractionDigits: 2,
                                              })}
                                            </Typography>
                                          </Box>
                                        )
                                      )}
                                      <Typography
                                        variant='body2'
                                        sx={{ fontWeight: 600, mt: 1 }}
                                      >
                                        {i18n.t(
                                          'pipelines.details.activityHistory.sale.productDetails.total'
                                        )}
                                        : R${' '}
                                        {parseFloat(value).toLocaleString(
                                          'pt-BR',
                                          {
                                            minimumFractionDigits: 2,
                                          }
                                        )}
                                      </Typography>
                                    </Box>
                                  );
                                }

                                // Anexo: box com Nome do arquivo e Link clicável
                                if (requirementType === 'attach') {
                                  let attachUrl =
                                    activity.data?.url ||
                                    activity.data?.fileUrl ||
                                    (typeof value === 'object' && value !== null
                                      ? value?.url || value?.fileUrl
                                      : null);
                                  if (attachUrl && typeof attachUrl === 'string') {
                                    attachUrl = attachUrl.trim();
                                    if (
                                      attachUrl &&
                                      !attachUrl.startsWith('http://') &&
                                      !attachUrl.startsWith('https://')
                                    ) {
                                      attachUrl = 'https://' + attachUrl;
                                    }
                                  }
                                  const attachName =
                                    activity.data?.fileName ||
                                    (typeof value === 'string' ? value : value?.name) ||
                                    activity.data?.requirementName ||
                                    'Anexo';
                                  return (
                                    <Box>
                                      <Typography variant='body2' sx={{ mb: 0.5 }}>
                                        <strong>Nome do arquivo:</strong> {attachName}
                                      </Typography>
                                      {attachUrl && attachUrl.startsWith('http') ? (
                                        <Typography variant='body2'>
                                          <strong>Link:</strong>{' '}
                                          <Link
                                            href={attachUrl}
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            sx={{ wordBreak: 'break-all' }}
                                          >
                                            {attachUrl}
                                          </Link>
                                        </Typography>
                                      ) : (
                                        attachUrl && (
                                          <Typography variant='body2' sx={{ wordBreak: 'break-all' }}>
                                            <strong>Link:</strong> {attachUrl}
                                          </Typography>
                                        )
                                      )}
                                    </Box>
                                  );
                                }

                                // Se for uma data, formatar de acordo com o idioma do i18n
                                if (requirementType === 'date' && value) {
                                  try {
                                    const date = parseISO(value);
                                    const currentLanguage =
                                      i18n.language || 'pt';

                                    // Mapear idiomas do i18n para locales do date-fns
                                    const localeMap = {
                                      pt: ptBR,
                                      en: enUS,
                                      es: es,
                                    };

                                    const locale =
                                      localeMap[currentLanguage] || ptBR;

                                    return format(date, 'dd/MM/yyyy', {
                                      locale,
                                    });
                                  } catch (error) {
                                    console.warn(
                                      'Erro ao formatar data:',
                                      error
                                    );
                                    return String(value);
                                  }
                                }

                                // Lógica para valores booleanos
                                if (value === 'true' || value === true) {
                                  return i18n.t(
                                    'pipelines.details.activityHistory.booleanValues.true'
                                  );
                                } else if (
                                  value === 'false' ||
                                  value === false
                                ) {
                                  return i18n.t(
                                    'pipelines.details.activityHistory.booleanValues.false'
                                  );
                                } else {
                                  return String(value);
                                }
                              })()}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                  </Box>
                }
                secondary={
                  activity.user && (
                    <Typography
                      variant='caption'
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {i18n.t('pipelines.details.activityHistory.by')}{' '}
                      {activity.user.name}
                    </Typography>
                  )
                }
              />
            </ListItem>
            {index < activitiesToRender.length - 1 && <Divider />}
          </React.Fragment>
        ); })}
      </List>
    </Box>
  );
};

export default DealActivityHistory;
